// @flow
import * as React from 'react';
import * as ss from 'styled-system';
import styled from 'styled-components';
import ScrollContext from 'containers/ScrollContext';
import ViewportContext from 'containers/ViewportContext';
import Box from 'components/Box';

const { useEffect, useState, useRef, useContext } = React;

export const PARALLAX_DEFAULT_SPEED = 300;

const Container = styled(
  React.forwardRef(
    (
      { className, children }: { className: string, children: React.Node },
      ref
    ) => (
      <div className={className} ref={ref}>
        {children}
      </div>
    )
  )
)`
  ${ss.position} 
  ${ss.top} 
  ${ss.right} 
  ${ss.bottom} 
  ${ss.left}
  ${ss.zIndex}
  ${ss.space} 
  ${ss.display}
  ${ss.width} 
  ${ss.height} 
  ${ss.maxWidth} 
  ${ss.maxHeight} 
  ${ss.minWidth} 
  ${ss.minHeight}
`;

const TransformContainer = styled.div.attrs(({ translateY, unit }) => ({
  style: {
    transform: `translateY(${translateY}${unit}) translateZ(0)`
  }
}))`
  height: 100%;
  width: 100%;
  will-change: transform;
`;

const Parallax = ({
  speed = PARALLAX_DEFAULT_SPEED,
  y,
  children,
  unit = 'px',
  disabled,
  ...props
}: {
  speed?: number,
  y?: [?number, ?number],
  unit?: 'px' | 'vh' | 'vw' | '%',
  // children?: ({}) => ?React.Node,
  children?: React.Node,
  disabled?: boolean
}) => {
  const [, scrollY] = useContext(ScrollContext);
  const viewportSize = useContext(ViewportContext);
  const [translateY, setTranslateY] = useState(null);
  const ref = useRef(null);

  useEffect(
    () => {
      window.requestAnimationFrame(() => {
        if (ref && ref.current) {
          const height = ref.current.offsetHeight;
          const offsetY = ref.current.getBoundingClientRect().top;
          const inView = offsetY > 0 - height && offsetY < window.innerHeight;
          const maxHeight = window.innerHeight + 2 * height;
          if (inView) {
            const tY = speed * (1 - (offsetY + height) / maxHeight);
            if (translateY !== tY) {
              setTranslateY(tY);
            }
          }
        }
      });
    },
    [scrollY, speed, ref, viewportSize, translateY]
  );

  return disabled ? (
    <Box {...props}>{children}</Box>
  ) : (
    <Container ref={ref} {...props}>
      <TransformContainer translateY={translateY} unit={unit}>
        {translateY && children}
      </TransformContainer>
    </Container>
  );
};

export default Parallax;
